import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '../../../context/ToastContext';
import { Chat as ApiChat } from '../apis/types';
import { Chat } from '../types';
import { transformApiChatToUI } from '../utils/messageUtils';

import { useRenameChat } from './useRenameChat';
import { useStarChat } from './useStarChat';

interface UseOpenedChatsProps {
  chats: Chat[];
  activeChatId: string | null;
  createNewChat: (data: {
    title: string;
    starred: boolean;
  }) => Promise<ApiChat>;
}

interface UseOpenedChatsReturn {
  openedChats: Chat[];
  setOpenedChats: React.Dispatch<React.SetStateAction<Chat[]>>;
  handleCreateNewChat: () => Promise<void>;
  handleCloseTab: (chatId: string) => void;
  handleRenameChat: (chatId: string, newTitle: string) => Promise<void>;
  handleStarChat: (chatId: string, starred: boolean) => Promise<void>;
}

export const useOpenedChats = ({
  chats,
  activeChatId,
  createNewChat,
}: UseOpenedChatsProps): UseOpenedChatsReturn => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [openedChats, setOpenedChats] = useState<Chat[]>([]);

  const { renameChat } = useRenameChat();
  const { starChat } = useStarChat();

  // Sync opened chats with the latest data from React Query
  // Remove tabs for deleted chats and update data for existing ones
  useEffect(() => {
    setOpenedChats(prevOpenedChats => {
      const chatIds = new Set(chats.map(c => c.id));
      const updated = prevOpenedChats
        .filter(openedChat => chatIds.has(openedChat.id))
        .map(openedChat => {
          const updatedChat = chats.find(chat => chat.id === openedChat.id);
          return updatedChat || openedChat;
        });

      if (updated.length !== prevOpenedChats.length) return updated;

      const hasChanges = updated.some((chat, index) => {
        const prevChat = prevOpenedChats[index];
        return (
          !prevChat ||
          chat.title !== prevChat.title ||
          chat.starred !== prevChat.starred ||
          chat.updatedAt !== prevChat.updatedAt
        );
      });

      return hasChanges ? updated : prevOpenedChats;
    });
  }, [chats]);

  // Auto-open tab when navigating to a chat via URL
  useEffect(() => {
    if (activeChatId && chats.length > 0) {
      const selectedChat = chats.find(chat => chat.id === activeChatId);
      if (selectedChat) {
        setOpenedChats(prev => {
          const exists = prev.some(c => c.id === selectedChat.id);
          if (!exists) {
            return [...prev, selectedChat];
          }
          return prev;
        });
      }
    }
  }, [activeChatId, chats]);

  const handleCreateNewChat = useCallback(async (): Promise<void> => {
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const MM = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const HH = String(now.getHours()).padStart(2, '0');
    const mm = String(now.getMinutes()).padStart(2, '0');
    const title = `New Chat @ ${yy}${MM}${dd} ${HH}:${mm}`;

    const newChat = await createNewChat({
      title,
      starred: false,
    });

    if (newChat) {
      const uiChat = transformApiChatToUI(newChat);
      setOpenedChats(prev => [...prev, uiChat]);
      setTimeout(() => {
        navigate(`/conversation/${newChat.id}`);
      }, 0);
    }
  }, [createNewChat, navigate]);

  const handleCloseTab = useCallback(
    (chatId: string): void => {
      const isClosingActiveTab = chatId === activeChatId;
      const updatedChats = openedChats.filter(chat => chat.id !== chatId);

      setOpenedChats(updatedChats);

      if (isClosingActiveTab) {
        if (updatedChats.length > 0) {
          navigate(`/conversation/${updatedChats[0].id}`);
        } else {
          navigate('/conversation');
        }
      }
    },
    [activeChatId, openedChats, navigate]
  );

  const handleRenameChat = useCallback(
    async (chatId: string, newTitle: string): Promise<void> => {
      try {
        await renameChat({ chatId, title: newTitle });
      } catch (error) {
        showToast(
          'Rename Failed',
          'Failed to rename chat. Please try again.',
          'error'
        );
      }
    },
    [renameChat, showToast]
  );

  const handleStarChat = useCallback(
    async (chatId: string, starred: boolean): Promise<void> => {
      try {
        await starChat({ chatId, starred });
      } catch (error) {
        showToast(
          'Star Failed',
          'Failed to update chat star status. Please try again.',
          'error'
        );
      }
    },
    [starChat, showToast]
  );

  return {
    openedChats,
    setOpenedChats,
    handleCreateNewChat,
    handleCloseTab,
    handleRenameChat,
    handleStarChat,
  };
};
